import type { NavLink } from '@/hooks/useHeaderNavigation'
import { cn } from '@/lib/utils'
import { ChevronDown } from 'lucide-react'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { NavSubmenuItem } from './NavSubmenuItem'

type Props = {
  readonly navLinks: NavLink[]
  readonly isLinkActive: (link: NavLink) => boolean
  readonly isSubLinkActive: (subLink: { path: string }) => boolean
  readonly isOpen: boolean
  readonly onNavigate: () => void
  readonly onEscape: () => void
}

export function MobileNav({ navLinks, isLinkActive, isSubLinkActive, isOpen, onNavigate, onEscape }: Props) {
  const { t } = useTranslation()
  const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState<string | null>(null)
  const firstLinkRef = useRef<HTMLElement | null>(null)

  // Reset submenu state when mobile menu closes
  useEffect(() => {
    if (!isOpen) {
      setMobileSubmenuOpen(null)
    }
  }, [isOpen])

  // Focus first nav link when mobile menu opens
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => {
        firstLinkRef.current?.focus()
      })
    }
  }, [isOpen])

  // Close mobile menu on Escape key
  useEffect(() => {
    if (!isOpen) return

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onEscape()
      }
    }

    document.addEventListener('keydown', handleEscapeKey)
    return () => {
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [isOpen, onEscape])

  if (!isOpen) return null

  return (
    <nav
      id='mobile-navigation'
      aria-label={t('header.mobileNavigation')}
      className='mt-4 border-t border-gray-200 pt-4 pb-4 md:hidden'
    >
      <ul role='list' className='flex list-none flex-col space-y-3'>
        {navLinks.map((link, index) => {
          const isActive = isLinkActive(link)
          const isFirstLink = index === 0

          if (link.submenu) {
            const isSubmenuOpen = mobileSubmenuOpen === link.path
            const submenuId = `mobile-submenu-${link.path.replace(/\//g, '-').replace(/^-/, '')}`

            return (
              <li key={link.path}>
                <button
                  ref={isFirstLink ? (firstLinkRef as React.RefObject<HTMLButtonElement>) : undefined}
                  type='button'
                  onClick={() => setMobileSubmenuOpen(isSubmenuOpen ? null : link.path)}
                  aria-expanded={isSubmenuOpen}
                  className={cn(
                    'flex w-full items-center justify-between rounded-md px-2 py-2 text-left text-base font-bold text-gray-700 transition-colors hover:text-[#0e2c89]',
                    isActive ? 'bg-blue-50 text-[#0e2c89]' : ''
                  )}
                >
                  {link.label}
                  <ChevronDown
                    size={16}
                    aria-hidden='true'
                    className={cn('transition-transform', isSubmenuOpen ? 'rotate-180' : '')}
                  />
                </button>
                {isSubmenuOpen && (
                  <ul id={submenuId} role='list' aria-label={link.label} className='mt-2 ml-4 list-none space-y-1'>
                    {link.submenu.map((subLink) => (
                      <NavSubmenuItem
                        key={subLink.path}
                        path={subLink.path}
                        label={subLink.label}
                        isActive={isSubLinkActive(subLink)}
                        variant='mobile'
                        onClick={() => onNavigate()}
                      />
                    ))}
                  </ul>
                )}
              </li>
            )
          }

          return (
            <li key={link.path}>
              <Link
                ref={isFirstLink ? (firstLinkRef as React.RefObject<HTMLAnchorElement>) : undefined}
                to={link.path}
                onClick={() => onNavigate()}
                aria-current={isActive ? 'page' : undefined}
                className={cn(
                  'rounded-md px-2 py-2 text-base font-bold text-gray-700 transition-colors hover:text-[#0e2c89]',
                  isActive ? 'bg-blue-50 text-[#0e2c89]' : ''
                )}
              >
                {link.label}
              </Link>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
